import { sendDeviceCommandRequest } from "./mdmApi";
import {
  FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT,
  processMultiLocationLogResults,
} from "./mdmCommandRunners";
import {
  DEVICE_INFO_NO_STREAM_ENDPOINT,
  getActionKeyForEndpoint,
  isSuccessfulCommandResult,
  normalizeDeviceResult,
  parseSseEventBlock,
} from "./mdmCommandUtils";

export const collectDeviceInfoNoStreamCandidates = (payload) => {
  const candidates = [];
  if (!payload) return candidates;

  if (Array.isArray(payload)) {
    payload.forEach((item) => candidates.push(item));
    return candidates;
  }

  if (typeof payload !== "object") return candidates;

  if (Array.isArray(payload.results)) {
    payload.results.forEach((item) => candidates.push(item));
    return candidates;
  }

  if (payload.devices && typeof payload.devices === "object") {
    Object.entries(payload.devices).forEach(([deviceId, value]) => {
      candidates.push({ [deviceId]: value });
    });
    return candidates;
  }

  Object.entries(payload).forEach(([key, value]) => {
    if (key === "message" || key === "response") return;
    candidates.push({ [key]: value });
  });

  return candidates;
};

const readDeviceInfoNoStreamResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("text/event-stream")) {
    if (!response.body) throw new Error("Failed to read response stream.");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const results = [];

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() || "";

      chunks.forEach((rawBlock) => {
        const parsedEvent = parseSseEventBlock(rawBlock);
        if (parsedEvent?.event === "device_result" && typeof parsedEvent.data === "object") {
          results.push(parsedEvent.data);
        }
      });
    }

    const parsedRemainder = parseSseEventBlock(buffer);
    if (
      parsedRemainder?.event === "device_result" &&
      typeof parsedRemainder.data === "object"
    ) {
      results.push(parsedRemainder.data);
    }

    return results;
  }

  if (contentType.includes("application/json")) {
    return await response.json();
  }

  return await response.text();
};

export const refreshMdmDeviceInfoForDevices = async ({
  serverUrl,
  deviceIds,
  getToken,
  throwIfRequestFailed,
  mergeDeviceResultsIntoCache,
  allDevicesRef,
  setAllDevices,
  fetchDeviceInfoCache,
  upsertDeviceActionResult,
}) => {
  const normalizedIds = deviceIds.map((id) => String(id).trim()).filter(Boolean);
  if (!normalizedIds.length) {
    throw new Error("Select at least one device before refreshing.");
  }

  const token = getToken();
  const response = await sendDeviceCommandRequest(
    serverUrl,
    DEVICE_INFO_NO_STREAM_ENDPOINT,
    token,
    { devices: normalizedIds }
  );
  await throwIfRequestFailed(response, "Failed to refresh MDM device info");

  const payload = await readDeviceInfoNoStreamResponse(response);
  const candidates = Array.isArray(payload)
    ? payload
    : collectDeviceInfoNoStreamCandidates(payload);

  const actionKey = getActionKeyForEndpoint(DEVICE_INFO_NO_STREAM_ENDPOINT);
  const normalizedResults = candidates
    .map((candidate) => normalizeDeviceResult(candidate))
    .filter((result) => result.deviceId && result.normalizedResult);

  normalizedResults.forEach(({ deviceId, normalizedResult }) => {
    upsertDeviceActionResult?.(deviceId, actionKey, {
      endpoint: DEVICE_INFO_NO_STREAM_ENDPOINT,
      action: actionKey,
      status:
        normalizedResult?.status ||
        (isSuccessfulCommandResult(normalizedResult) ? "ok" : "failed"),
      data: normalizedResult?.data ?? normalizedResult,
      raw_result: normalizedResult,
      updated_at: normalizedResult?.updated_at || new Date().toISOString(),
    });
  });

  const successfulResults = normalizedResults
    .filter(
      ({ normalizedResult }) =>
        isSuccessfulCommandResult(normalizedResult) || normalizedResult?.status === "ok"
    )
    .map(({ normalizedResult }) => normalizedResult);

  if (successfulResults.length > 0) {
    const merged = mergeDeviceResultsIntoCache(allDevicesRef.current, successfulResults);
    if (merged.updatedCount > 0) {
      allDevicesRef.current = merged.nextDevices;
      setAllDevices(merged.nextDevices);
    }
  }

  await fetchDeviceInfoCache(token).catch((err) => console.error(err));

  return {
    requested: normalizedIds.length,
    successCount: successfulResults.length,
    failedCount: normalizedIds.length - successfulResults.length,
  };
};

export const refreshLogsLocationForDevices = async ({
  serverUrl,
  deviceIds,
  retailerId,
  branchId,
  getToken,
  throwIfRequestFailed,
  allDevicesRef,
  setAllDevices,
  upsertDeviceActionResult,
}) => {
  const normalizedIds = deviceIds.map((id) => String(id).trim()).filter(Boolean);
  if (!normalizedIds.length) {
    throw new Error("Select at least one device before refreshing.");
  }

  const resolvedRetailerId = Number(retailerId);
  const resolvedBranchId = Number(branchId);
  if (!Number.isFinite(resolvedRetailerId) || !Number.isFinite(resolvedBranchId)) {
    throw new Error("Branch retailer_id and branch_id are required to refresh location logs.");
  }

  const token = getToken();
  const response = await sendDeviceCommandRequest(
    serverUrl,
    FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT,
    token,
    {
      device_ids: normalizedIds,
      retailer_id: resolvedRetailerId,
      branch_id: resolvedBranchId,
    }
  );
  await throwIfRequestFailed(response, "Failed to refresh location logs");

  const payload = await response.json();
  const processedCounts = processMultiLocationLogResults({
    payload,
    endpoint: FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT,
    upsertDeviceActionResult,
    allDevicesRef,
    setAllDevices,
  });

  const reportedSuccess = Number(payload?.success_count);
  const reportedFailure = Number(payload?.failure_count);
  const successCount = Number.isFinite(reportedSuccess)
    ? reportedSuccess
    : processedCounts.success;
  const failedCount = Number.isFinite(reportedFailure)
    ? reportedFailure
    : processedCounts.failed;

  return {
    requested: normalizedIds.length,
    successCount,
    failedCount,
  };
};
